import { signInWithMagicLink, signInWithPassword, signUpWithPassword } from '@services/auth';
import { toast } from '@ui/components/Toast';
import { navigate } from '@ui/lib/router';
import { useAuth } from '@ui/lib/useAuth';
import { useEffect, useState } from 'preact/hooks';

type Mode = 'signin' | 'signup';
type Stage = 'idle' | 'busy' | 'magic_sent';

export function LoginScreen() {
  const { user, ready } = useAuth();
  const [mode, setMode] = useState<Mode>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [stage, setStage] = useState<Stage>('idle');
  const [sentTo, setSentTo] = useState('');

  // já logado → volta pra home (o gate no App também cobre isso)
  useEffect(() => {
    if (ready && user) navigate('home');
  }, [ready, user]);

  function validEmail(): string | null {
    const e = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) {
      toast('Email inválido', 'err');
      return null;
    }
    return e;
  }

  async function submit() {
    const e = validEmail();
    if (!e) return;
    if (password.length < 6) {
      toast('A senha precisa de pelo menos 6 caracteres', 'err');
      return;
    }
    setStage('busy');
    if (mode === 'signin') {
      const { error } = await signInWithPassword(e, password);
      setStage('idle');
      if (error) toast(error, 'err', 5000);
      // sucesso: onAuthStateChange redireciona via useEffect
    } else {
      const { error, needsConfirmation } = await signUpWithPassword(e, password);
      setStage('idle');
      if (error) {
        toast(error, 'err', 5000);
        return;
      }
      if (needsConfirmation) {
        setSentTo(e);
        setStage('magic_sent');
        toast('Conta criada! Confirma no email pra entrar.', 'ok', 5000);
      } else {
        toast('✓ Conta criada!', 'ok');
      }
    }
  }

  async function sendMagic() {
    const e = validEmail();
    if (!e) return;
    setStage('busy');
    const { error } = await signInWithMagicLink(e);
    if (error) {
      setStage('idle');
      toast(error, 'err', 6000);
      return;
    }
    setSentTo(e);
    setStage('magic_sent');
  }

  return (
    <div style={{ maxWidth: '460px', margin: '40px auto 0' }}>
      <div class="panel" style={{ padding: '32px' }}>
        <div
          style={{
            fontFamily: 'var(--f-mono)',
            fontSize: '10px',
            color: 'var(--cyan)',
            letterSpacing: '2.5px',
            marginBottom: '8px',
          }}
        >
          ▰ CONTA ROBOX
        </div>
        <h1 class="heading" style={{ fontSize: '24px', marginBottom: '8px', color: 'var(--text)' }}>
          {mode === 'signin' ? 'Entrar' : 'Criar conta'}
        </h1>
        <p
          style={{
            color: 'var(--text-soft)',
            fontSize: '13.5px',
            lineHeight: 1.6,
            marginBottom: '20px',
          }}
        >
          Seus conteúdos, sessões e Score sincronizam automaticamente entre dispositivos.
        </p>

        {stage === 'magic_sent' ? (
          <div
            style={{
              padding: '20px',
              border: '1px solid var(--green)',
              borderRadius: 'var(--r-md)',
              background: 'rgba(0,255,157,0.06)',
            }}
          >
            <div
              style={{
                fontFamily: 'var(--f-heading)',
                fontSize: '15px',
                color: 'var(--green)',
                letterSpacing: '1px',
                marginBottom: '8px',
              }}
            >
              ✓ EMAIL ENVIADO
            </div>
            <p style={{ fontSize: '14px', color: 'var(--text)', lineHeight: 1.6 }}>
              Enviamos um link de acesso para <b>{sentTo}</b>.
              <br />
              Abre o email e clica — você entra sem senha. Depois dá pra definir uma senha nova nas
              Configurações.
            </p>
            <div
              style={{
                marginTop: '12px',
                fontFamily: 'var(--f-mono)',
                fontSize: '10.5px',
                color: 'var(--text-dim)',
                lineHeight: 1.7,
              }}
            >
              ▸ pode levar 1-2 min · confere o spam
            </div>
            <button
              class="btn ghost sm"
              style={{ marginTop: '14px' }}
              onClick={() => setStage('idle')}
            >
              ← voltar
            </button>
          </div>
        ) : (
          <>
            {/* Toggle Entrar / Criar conta */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '6px',
                marginBottom: '18px',
                padding: '4px',
                background: 'rgba(0,8,24,0.6)',
                border: '1px solid var(--border-soft)',
                borderRadius: 'var(--r-md)',
              }}
            >
              {(['signin', 'signup'] as Mode[]).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setMode(m)}
                  style={{
                    padding: '9px',
                    borderRadius: 'var(--r-sm)',
                    border: 'none',
                    cursor: 'pointer',
                    fontFamily: 'var(--f-heading)',
                    fontSize: '11px',
                    letterSpacing: '1.5px',
                    fontWeight: 700,
                    background:
                      mode === m
                        ? 'linear-gradient(135deg, var(--cyan), var(--purple))'
                        : 'transparent',
                    color: mode === m ? 'var(--bg-0)' : 'var(--text-dim)',
                    transition: 'all var(--tr-fast)',
                  }}
                >
                  {m === 'signin' ? 'ENTRAR' : 'CRIAR CONTA'}
                </button>
              ))}
            </div>

            <div class="field">
              <label>Email</label>
              <input
                class="input"
                type="email"
                placeholder="voce@email.com"
                value={email}
                disabled={stage === 'busy'}
                onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
              />
            </div>
            <div class="field">
              <label>Senha {mode === 'signup' && '(mínimo 6 caracteres)'}</label>
              <input
                class="input"
                type="password"
                placeholder="••••••••"
                value={password}
                disabled={stage === 'busy'}
                onInput={(e) => setPassword((e.target as HTMLInputElement).value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') submit();
                }}
              />
            </div>

            <button
              class="btn primary lg"
              style={{ width: '100%', justifyContent: 'center', marginTop: '6px' }}
              disabled={stage === 'busy'}
              onClick={submit}
            >
              {stage === 'busy' ? 'AGUARDE...' : mode === 'signin' ? '→ ENTRAR' : '⚡ CRIAR CONTA'}
            </button>

            <div style={{ textAlign: 'center', marginTop: '14px' }}>
              <button
                type="button"
                onClick={sendMagic}
                disabled={stage === 'busy'}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--text-dim)',
                  fontFamily: 'var(--f-mono)',
                  fontSize: '11px',
                  cursor: 'pointer',
                  letterSpacing: '0.5px',
                  textDecoration: 'underline',
                }}
              >
                {mode === 'signin'
                  ? 'esqueci a senha → entrar por link no email'
                  : 'prefiro entrar sem senha (link por email)'}
              </button>
            </div>

            {mode === 'signup' && (
              <div
                style={{
                  marginTop: '14px',
                  fontFamily: 'var(--f-mono)',
                  fontSize: '10.5px',
                  color: 'var(--text-dim)',
                  lineHeight: 1.7,
                }}
              >
                ▸ seus dados locais deste browser migram pra conta no primeiro login
              </div>
            )}
          </>
        )}
      </div>

      <div
        style={{
          textAlign: 'center',
          marginTop: '16px',
          fontFamily: 'var(--f-mono)',
          fontSize: '10px',
          color: 'var(--text-mute)',
          letterSpacing: '1.5px',
        }}
      >
        ROBOX · SISTEMA OPERACIONAL DE PERFORMANCE PROFISSIONAL
      </div>
    </div>
  );
}
